/*
 *  Copyright
 *
 *  Tegona S.A.
 *
 *  Tegona JS © 2017, Tegona S.A.
 *
 *  ALL RIGHTS RESERVED. THIS PROGRAM CONTAINS MATERIAL PROTECTED
 *  UNDER INTERNATIONAL AND SWITZERLAND COPYRIGHT LAWS AND TREATIES.
 *  ANY UNAUTHORIZED USE OF THE PROGRAM, INCLUDING REPRODUCTION,
 *  MODIFICATION, TRANSFER, TRANSMITTAL OR REPUBLICATION OF THIS
 *  MATERIAL IN ANY FORM OR BY ANY MEANS IS PROHIBITED WITHOUT
 *  SPECIFIC WRITTEN PERMISSION OF THE COPYRIGHT HOLDER.
 *
 *  copyright@tegona.com
*/

import React from 'react'
import R from 'ramda'
import moment from 'moment'
import { injectIntl } from 'react-intl'
import { RaisedButton, FontIcon, Checkbox, MenuItem } from 'material-ui'
import {
  Table,
  TableBody,
  TableHeader,
  TableHeaderColumn,
  TableRow,
  TableRowColumn
} from 'material-ui/Table'
import { Grid } from '@material-ui/core'
import { red500 } from 'material-ui/styles/colors'
import { TextField, SelectField, AutoComplete } from 'generic/form'
import AlertModal from 'generic/dialog/AlertModal'

import * as applicantActions from 'actions/Applicant'
import * as userActions from 'actions/User'
import * as identityActions from 'actions/Identity'
import * as entityActions from 'actions/Entity'
import * as applicationConstants from 'constants/Applicant'

class ApplicantForm extends React.Component {
  constructor (props) {
    super(props)

    this.state = {
      applicant: props.applicant,
      emptyEntity: undefined,
      isDisabled: true,
      showAlertMessage: false,
      errors: {},
      loading: false
    }

    this.handleCancel = this.handleCancel.bind(this)
    this.handleConfirm = this.handleConfirm.bind(this)
  }

  componentDidMount() {
    this.handleLoadApplicant()
  }

  handleLoadApplicant() {
    if (!this.props.applicant.uid) {
      Promise.all([
        entityActions.getEntity(),
        identityActions.getIdentity(),
        userActions.getUser()
      ])
      .then((results) => {
        this.setState({
          isDisabled: false,
          emptyEntity: results[0],
          applicant: {
            ...this.state.applicant,
            is_active: true,
            entities: [results[0]],
            identities: [results[1]],
            users: [results[2]]
          },
          entitySearchTexts: [results[0].name || '']
        })
      })
    } else {
      this.setState({
        entities: this.props.applicant.entities,
        entitySearchTexts: this.props.applicant.entities.map((entity) => entity.name || ''),
        applicant: this.props.applicant
      })
    }
  }

  handleChange(name, value) {
    this.setState({
      [name]: value
    })
  }

  handleApplicantChange(name, value) {
   const applicant = {
    ...this.state.applicant,
    [name]: value
   }

   this.handleChange('applicant', applicant)
  }

  handleCheckApplicant(name, value) {
    let users = this.state.applicant.users

    if (!value) {
       users = this.state.applicant.users.map((element) => {
        return {
          ...element,
          [name]: value
        }
      })
    }

    const applicant = {
      ...this.state.applicant,
      [name]: value,
      users: users
    }

    this.handleChange('applicant', applicant)
  }

  handleIdentityChange(index, obj, name, value) {
    this.setState({
      errors: {
        ...this.state.errors,
        ['identity.' + index] : {
          ...this.state.errors['identity.' + index],
          [name]: undefined
        }
      }
    })

    const objs = this.state.applicant.identities.map((element, i) => {
      if (index === i) {
        return {
          ...obj,
          [name]: value
        }
      }

      return element
    })

    this.handleApplicantChange('identities', objs)
  }

  handleUserChange(index, obj, name, value) {
    this.setState({
      errors: {
        ...this.state.errors,
        ['user.' + index] : {
          ...this.state.errors['user.' + index],
          [name]: undefined
        }
      }
    })

    const objs = this.state.applicant.users.map((element, i) => {
      if (index === i) {
        return {
          ...obj,
          [name]: value
        }
      }

      return element
    })

    this.handleApplicantChange('users', objs)
  }

  handleEntityChange(index, name, value) {
    if (typeof value === 'string'){
        return
    }
    const objs = this.state.applicant.entities.map((element, i) => {
      if (index === i) return value || this.state.emptyEntity

      return element
    })

   const applicant = {
    ...this.state.applicant,
    entities: objs,
    aba_code: R.path([0, 'aba_code'], objs)
   }

   this.handleChange('applicant', applicant)
   this.setState({ entitySearchTexts: objs.map(entity => entity.name || '') })
  }

  handleCheckUser(index, obj, name, value) {
    const promise = new Promise((resolve, reject) => {
      resolve(this.handleUserChange(index, obj, name, value))
    })

    promise.then(() => {
      if (!value && !this.state.applicant.users.find((user, i) => i !== index && user.is_active === true)) {
        this.handleCheckApplicant(name, value)
      } else if (value && !this.state.applicant.is_active) {
        this.handleCheckApplicant(name, value)
      }
    })
  }

  handleValidation() {
    function getFieldErrorObject (errors) {
      let errorObject = {}
      errors.forEach((error) => {
        errorObject[error.field_name] = error.errors[0].user_message
      })

      return errorObject
    }

    let errorObjs = {}

    const entityValidationPromises = this.state.applicant.entities.map((entity, index) => {
      return entityActions.postEntityValidation(entity)
        .catch((errors) => {
          if (R.path(['response', 'status'], errors) === 400) {
            const fieldErrors = getFieldErrorObject(R.path(['response', 'data', 'field_errors'], errors))

            errorObjs = {
              ...errorObjs,
              ['entity.' + index]: fieldErrors
            }
          }
        })
    })

    const identityValidationPromises = this.state.applicant.identities.map((identity, index) => {
      return identityActions.postIdentityValidation(identity)
        .catch((errors) => {
          if (R.path(['response', 'status'], errors) === 400) {
            const fieldErrors = getFieldErrorObject(R.path(['response', 'data', 'field_errors'], errors))

            errorObjs = {
              ...errorObjs,
              ['identity.' + index]: fieldErrors
            }
          }
        })
    })

    const userValidationPromises = this.state.applicant.users.map((user, index) => {
      const obj = {
        ...user,
        email: user.email || null
      }

      return userActions.postUserValidation(obj, obj.uid)
        .catch((errors) => {
          if (R.path(['response', 'status'], errors) === 400) {
            const fieldErrors = getFieldErrorObject(R.path(['response', 'data', 'field_errors'], errors))

            errorObjs = {
              ...errorObjs,
              ['user.' + index]: fieldErrors
            }
          }
        })
    })


    const promises = [
      ...entityValidationPromises,
      ...userValidationPromises,
      ...identityValidationPromises
    ]

    return Promise.all(promises)
      .then(() => {
        this.handleChange('errors', errorObjs)

        if (Object.keys(errorObjs).length > 0) {
          this.handleChange('showAlertMessage', false)
          return false
        }

        return true
      })
  }

  async handleConfirm() {
    const isValidated = await this.handleValidation()

    if (!isValidated) {
      return
    }

    this.handleChange('loading', true)

    const userPromises = this.state.applicant.users.map((user, index) => {
      const obj = {
        ...user,
        email: user.email || null
      }

      return userActions.postUser(obj, obj.uid)
    })

    const identityPromises = this.state.applicant.identities.map((identity, index) => {
      return identityActions.postIdentity(identity)
    })

    let applicant = this.state.applicant

    Promise.all(userPromises)
      .then((results) => {
        this.setState({
          applicant: {
            ...this.state.applicant,
            users: results
          }
        })

        applicant = {
          ...applicant,
          users: results
        }

        return Promise.all(identityPromises)
      })
      .then((results) => {
        this.setState({
          applicant: {
            ...this.state.applicant,
            identities: results
          }
        })

        applicant = {
          ...applicant,
          identities: results,
        }

        return applicantActions.postApplicant(applicant, applicant.uid)
      })
      .then((result) => {
        this.handleChange('applicant', result)
        this.handleChange('isDisabled', true)
      })
      .finally(() => {
        this.handleChange('loading', false)
        this.handleChange('showAlertMessage', false)
      })
  }

  handleCancel() {
    this.handleChange('isDisabled', true)
    this.handleLoadApplicant()
  }

  render () {
    console.log(this.state.entitySearchTexts)
    return (
      <Grid container style={{margin: 15}}>
        <Grid item md={4} xs={6}>
          <Grid container>
            <Grid item md={6}>
              <Checkbox
                name="is_active"
                label={this.props.intl.formatMessage(applicationConstants.messages.APPLICANT_IS_ACTIVE_LABEL)}
                checked={this.state.applicant.is_active}
                disabled={this.state.isDisabled}
                style={styles.checkbox}
                onCheck={(event, value) => this.handleCheckApplicant(event.target.name, value)}
              />
            </Grid>

            <Grid md={6}>
              <TextField
                floatingLabelText={this.props.intl.formatMessage(applicationConstants.messages.APPLICANT_ABA_CODE_LABEL)}
                value={this.state.applicant.aba_code}
                disabled={true}
                style={styles.fontText}
              />
            </Grid>     
          </Grid>
        </Grid>

        <Grid md={8} xs={6}
          style={styles.buttons}
        >
          <RaisedButton
            label={this.props.intl.formatMessage(applicationConstants.messages.BACK)}
            secondary={true}
            icon={<FontIcon className='material-icons'>arrow_back</FontIcon>}
            style={{minWidth: 160, marginLeft: 5, float: 'right'}}
            onClick={() => this.props.handleChange('applicant', undefined)}
          />

          {
            this.state.applicant.uid
              ? <React.Fragment>
                {
                  this.state.isDisabled
                    ? <RaisedButton
                      label={this.props.intl.formatMessage(applicationConstants.messages.EDIT)}
                      icon={<FontIcon className='material-icons'>edit</FontIcon>}
                      style={{minWidth: 160, marginLeft: 5, float: 'right'}}
                      primary={true}
                      labelColor="white"
                      onClick={() => this.handleChange('isDisabled', false)}
                    />
                    : <RaisedButton
                      label={this.props.intl.formatMessage(applicationConstants.messages.CANCEL)}
                      icon={<FontIcon className='material-icons'>cancel</FontIcon>}
                      style={{minWidth: 160, marginLeft: 5, float: 'right'}}
                      secondary={true}
                      labelColor="white"
                      onClick={this.handleCancel}
                    />
                }
                </React.Fragment>
              : undefined
          }

          {
            !this.state.isDisabled
              ? <RaisedButton
                label={this.props.intl.formatMessage(applicationConstants.messages.SAVE)}
                icon={<FontIcon className='material-icons'>save</FontIcon>}
                style={{minWidth: 160, float: 'right'}}
                primary={true}
                labelColor="white"
                onClick={
                  this.state.applicant.uid
                    ? () => this.handleChange('showAlertMessage', true)
                    : () => this.handleConfirm()
                }
              />
              : undefined
          }
        </Grid>

        <Table>
          <TableHeader
            displaySelectAll={false}
            adjustForCheckbox={false}
          >
            <TableRow>
              <TableHeaderColumn
                colSpan="6"
                style={styles.titleHeader}
              >
                {this.props.intl.formatMessage(applicationConstants.messages.ENTITY_LABEL)}
              </TableHeaderColumn>
            </TableRow>

            <TableRow
              style={styles.tableHeader}
            >
              <TableHeaderColumn
                colSpan="2"
                style={styles.tableHeader}
              >
                {this.props.intl.formatMessage(applicationConstants.messages.NAME_LABEL)}
              </TableHeaderColumn>
              <TableHeaderColumn
                style={styles.tableHeader}
              >
                {this.props.intl.formatMessage(applicationConstants.messages.ENTITY_ABA_CODE_LABEL)}
              </TableHeaderColumn>
              <TableHeaderColumn
                style={styles.tableHeader}
              >
                {this.props.intl.formatMessage(applicationConstants.messages.ENTITY_PHONE_LABEL)}
              </TableHeaderColumn>
              <TableHeaderColumn
                style={styles.tableHeader}
              >
                {this.props.intl.formatMessage(applicationConstants.messages.ENTITY_EMAIL_LABEL)}
              </TableHeaderColumn>
              <TableHeaderColumn
                style={styles.tableHeader}
              >
                {this.props.intl.formatMessage(applicationConstants.messages.ENTITY_ADDRESS_LABEL)}
              </TableHeaderColumn>
            </TableRow>
          </TableHeader>

          <TableBody
            displayRowCheckbox={false}
          >
            <React.Fragment>
              {
                this.state.applicant &&
                this.state.applicant.entities.map((entity, index) => {
                  const rowErrors = this.state.errors['entity.'+index] || undefined

                  return (
                    <TableRow key={index}>
                      <TableRowColumn colSpan="2">
                        {
                          this.state.isDisabled
                            ? entity.name
                            : <AutoComplete
                              name='entity'
                              searchText={R.path([index], this.state.entitySearchTexts)}
                              url={entityActions.ENTITY_LIST_URL}
                              disabled={this.state.isDisabled}
                              searchKey='name'
                              textKey='name'
                              onUpdateInput={(searchText) => this.setState(prev => ({
                                entitySearchTexts: prev.entitySearchTexts.map((text, i) =>
                                  i === index ? searchText : text
                                )
                              }))}
                              placeholderMandatory={R.path(['meta', 'fields', 'name', 'mandatory'], entity)}
                              meta={R.path(['meta', 'fields', 'name'], entity)}
                              updateItem={(name, value) => this.handleEntityChange(index, name, value)}
                              errorText={R.path(['name'], rowErrors)}
                              textFieldStyle={styles.fontText}
                              onBlur={() => this.setState({ 
                                entitySearchTexts: this.state.applicant.entities.length
                                  ? this.state.applicant.entities.map((entity) => entity.name || '')
                                  : ['']
                              })}
                            />
                        }

                      </TableRowColumn>
                      <TableRowColumn>
                        {entity.aba_code}
                      </TableRowColumn>
                      <TableRowColumn>
                        {entity.phone_numbers}
                      </TableRowColumn>
                      <TableRowColumn>
                        {entity.emails}
                      </TableRowColumn>
                      <TableRowColumn>
                        {R.path(['address', 'address'], entity)}
                      </TableRowColumn>
                    </TableRow>
                  )
                })
              }
            </React.Fragment>
          </TableBody>
        </Table>

        <Table>
          <TableHeader
            displaySelectAll={false}
            adjustForCheckbox={false}
          >
            <TableRow>
              <TableHeaderColumn
                colSpan="5"
                style={styles.titleHeader}
              >
                {this.props.intl.formatMessage(applicationConstants.messages.IDENTITY_LABEL)}
              </TableHeaderColumn>
            </TableRow>

            <TableRow
              style={styles.tableHeader}
            >
              <TableHeaderColumn
                style={styles.tableHeader}
              >
                {this.props.intl.formatMessage(applicationConstants.messages.IDENTITY_FIRST_NAME_LABEL)}
              </TableHeaderColumn>
              <TableHeaderColumn
                style={styles.tableHeader}
              >
                {this.props.intl.formatMessage(applicationConstants.messages.IDENTITY_LAST_NAME_LABEL)}
              </TableHeaderColumn>
              <TableHeaderColumn
                style={styles.tableHeader}
              >
                {this.props.intl.formatMessage(applicationConstants.messages.IDENTITY_PHONE_LABEL)}
              </TableHeaderColumn>
              <TableHeaderColumn
                style={styles.tableHeader}
              >
                {this.props.intl.formatMessage(applicationConstants.messages.IDENTITY_EMAIL_LABEL)}
              </TableHeaderColumn>
              <TableHeaderColumn
                style={styles.tableHeader}
              >
                {this.props.intl.formatMessage(applicationConstants.messages.IDENTITY_FUNCTION_LABEL)}
              </TableHeaderColumn>
            </TableRow>
          </TableHeader>

          <TableBody
            displayRowCheckbox={false}
          >
            <React.Fragment>
              {
                this.state.applicant &&
                this.state.applicant.identities.map((identity, index) => {
                  const rowErrors = this.state.errors['identity.'+index] || undefined

                  return (
                    <TableRow key={index}>
                      <TableRowColumn>
                        {
                          this.state.isDisabled
                            ? identity.first_name
                            : <TextField
                            name='first_name'
                            value={identity.first_name}
                            style={styles.fontText}
                            multiLine={true}
                            onChange={(event, value) => this.handleIdentityChange(index, identity, event.target.name, value)}
                            errorText={R.path(['first_name'], rowErrors)}
                            placeholderMandatory={R.path(['meta', 'fields', 'first_name', 'mandatory'], identity)}
                          />
                        }
                      </TableRowColumn>
                      <TableRowColumn>
                        {
                          this.state.isDisabled
                            ? identity.last_name
                            : <TextField
                            name='last_name'
                            value={identity.last_name}
                            style={styles.fontText}
                            multiLine={true}
                            onChange={(event, value) => this.handleIdentityChange(index, identity, event.target.name, value)}
                            errorText={R.path(['last_name'], rowErrors)}
                            placeholderMandatory={R.path(['meta', 'fields', 'last_name', 'mandatory'], identity)}
                          />
                        }
                      </TableRowColumn>
                      <TableRowColumn>
                        {
                          this.state.isDisabled
                            ? identity.phone_numbers
                            : <TextField
                            name='phone_numbers'
                            value={identity.phone_numbers}
                            style={styles.fontText}
                            divStyle={styles.textFieldWrapper}
                            multiLine={true}
                            onChange={(event, value) => this.handleIdentityChange(index, identity, event.target.name, value)}
                            errorText={R.path(['phone_numbers'], rowErrors)}
                            placeholderMandatory={R.path(['meta', 'fields', 'phone_numbers', 'mandatory'], identity)}
                          />
                        }
                      </TableRowColumn>
                      <TableRowColumn>
                        {
                          this.state.isDisabled
                            ? <div style={styles.textFieldWrapper}>
                                {identity.emails}
                            </div>
                            : <TextField
                            name='emails'
                            value={identity.emails}
                            style={styles.fontText}
                            divStyle={styles.textFieldWrapper}
                            multiLine={true}
                            onChange={(event, value) => this.handleIdentityChange(index, identity, event.target.name, value)}
                            errorText={R.path(['emails'], rowErrors)}
                            placeholderMandatory={R.path(['meta', 'fields', 'emails', 'mandatory'], identity)}
                          />
                        }
                      </TableRowColumn>
                      <TableRowColumn>
                        {
                          this.state.isDisabled
                            ? identity.function
                            : <TextField
                            name='function'
                            value={identity.function}
                            style={styles.fontText}
                            multiLine={true}
                            onChange={(event, value) => this.handleIdentityChange(index, identity, event.target.name, value)}
                            errorText={R.path(['function'], rowErrors)}
                            placeholderMandatory={R.path(['meta', 'fields', 'function', 'mandatory'], identity)}
                          />
                        }
                      </TableRowColumn>
                    </TableRow>
                  )
                })
              }
            </React.Fragment>
          </TableBody>
        </Table>

        <Table>
          <TableHeader
            displaySelectAll={false}
            adjustForCheckbox={false}
          >
            <TableRow>
              <TableHeaderColumn
                colSpan="5"
                style={styles.titleHeader}
              >
                {this.props.intl.formatMessage(applicationConstants.messages.USER_LABEL)}
              </TableHeaderColumn>
            </TableRow>

            <TableRow
              style={styles.tableHeader}
            >
              <TableHeaderColumn
                style={styles.tableHeader}
              >
                {this.props.intl.formatMessage(applicationConstants.messages.USER_USERNAME_LABEL)}
              </TableHeaderColumn>
              <TableHeaderColumn
                style={styles.tableHeader}
              >
                {this.props.intl.formatMessage(applicationConstants.messages.USER_EMAIL_LABEL)}
              </TableHeaderColumn>
              <TableHeaderColumn
                style={styles.tableHeader}
              >
                {this.props.intl.formatMessage(applicationConstants.messages.USER_IS_ACTIVE_LABEL)}
              </TableHeaderColumn>
              <TableHeaderColumn
                style={styles.tableHeader}
              >
                {this.props.intl.formatMessage(applicationConstants.messages.USER_TYPE_VIEW_LABEL)}
              </TableHeaderColumn>
              <TableHeaderColumn
                style={styles.tableHeader}
              >
                {this.props.intl.formatMessage(applicationConstants.messages.USER_LAST_LOGIN_LABEL)}
              </TableHeaderColumn>
            </TableRow>
          </TableHeader>

          <TableBody
            displayRowCheckbox={false}
          >
            <React.Fragment>
              {
                this.state.applicant &&
                this.state.applicant.users.map((user, index) => {
                  const rowErrors = this.state.errors['user.'+index] || undefined
                  const userTypeView = this.props.userTypeViews.find(userTypeView => userTypeView.key === user.prestation_list_type)

                  return (
                    <TableRow key={index}>
                      <TableRowColumn>
                        {
                          this.state.isDisabled
                            ? user.username
                            : <TextField
                            name='username'
                            value={user.username}
                            style={styles.fontText}
                            divStyle={styles.textFieldWrapper}
                            multiLine={true}
                            onChange={(event, value) => this.handleUserChange(index, user, event.target.name, value)}
                            errorText={R.path(['username'], rowErrors)}
                            placeholderMandatory={R.path(['meta', 'fields', 'username', 'mandatory'], user)}
                          />
                        }
                      </TableRowColumn>
                      <TableRowColumn>
                        {
                          this.state.isDisabled
                            ? user.email
                            : <TextField
                            name='email'
                            value={user.email}
                            style={styles.fontText}
                            multiLine={true}
                            onChange={(event, value) => this.handleUserChange(index, user, event.target.name, value)}
                            errorText={R.path(['email'], rowErrors)}
                            placeholderMandatory={R.path(['meta', 'fields', 'email', 'mandatory'], user)}
                          />
                        }
                      </TableRowColumn>
                      <TableRowColumn>
                        <Checkbox
                          name="is_active"
                          checked={user.is_active}
                          disabled={this.state.isDisabled}
                          onCheck={(event, value) => this.handleCheckUser(index, user, event.target.name, value)}
                          errorText={R.path(['is_active'], rowErrors)}
                        />
                      </TableRowColumn>
                      <TableRowColumn>
                        {
                          this.state.isDisabled
                            ? userTypeView.value
                            : <SelectField
                              name='prestation_list_type'
                              sourceKey='key'
                              value={user.prestation_list_type}
                              style={styles.fontText}
                              multiLine={true}
                              onChange={(event, key, value) => this.handleUserChange(index, user, event.target.name, value)}
                              errorText={R.path(['prestation_list_type'], rowErrors)}
                              placeholderMandatory={R.path(['meta', 'fields', 'prestation_list_type', 'mandatory'], user)}

                            >
                              {
                                this.props.userTypeViews.map((type, i) => (
                                  <MenuItem
                                    key={i}
                                    primaryText={type.value}
                                    value={type.key}
                                  />
                                ))
                              }
                            </SelectField>
                        }
                      </TableRowColumn>
                      <TableRowColumn>
                        {
                          user.last_login
                            ? moment(user.last_login, 'YYYY-MM-DD HH:mm:ss').format('DD.MM.YYYY HH:mm')
                            : undefined
                        }
                      </TableRowColumn>
                    </TableRow>
                  )
                })
              }
            </React.Fragment>
          </TableBody>
        </Table>

        <Grid xs={12}>
          <TextField
            name='admin_remarks'
            multiLine={true}
            rows={2}
            value={this.state.applicant.admin_remarks}
            disabled={this.state.isDisabled}
            onChange={(event, value) => this.handleApplicantChange(event.target.name, value)}
            floatingLabelText={this.props.intl.formatMessage(applicationConstants.messages.APPLICANT_ADMIN_REMARKS_MESSAGE)}
          />
        </Grid>

        <AlertModal
          open={this.state.showAlertMessage}
          title={this.props.intl.formatMessage(applicationConstants.messages.SAVE_ALERT)}
          type={AlertModal.CONFIRM}
          handleCancel={() => this.handleChange('showAlertMessage', false)}
          handleConfirm={this.handleConfirm}
          cancellationLabel={this.props.intl.formatMessage(applicationConstants.messages.CLOSE_ALERT)}
          confirmationLabel={this.props.intl.formatMessage(applicationConstants.messages.CONFIRM_ALERT)}
          confirmDisabled={this.state.loading}
          cancelDisabled={this.state.loading}
        >
          <React.Fragment>
            <p style={{textAlign: 'center'}}>
              <FontIcon
                className='material-icons'
                color={red500}
                style={{fontSize: 50}}
              >
                warning
              </FontIcon>
            </p>
            <p style={{color: red500, textAlign: 'justify'}}>
              {this.props.intl.formatMessage(applicationConstants.messages.ALERT_MESSAGE1)}
            </p>
            <p style={{textAlign: 'center'}}>
              {this.props.intl.formatMessage(applicationConstants.messages.ALERT_MESSAGE2)}
            </p>
          </React.Fragment>
        </AlertModal>
      </Grid>
    )
  }
}

const styles = {
  fontText: {
    fontSize: 13,
  },
  textFieldWrapper: {
    height: 'auto',
    width: '100%',
    wordWrap: 'break-word',
    overflowWrap: 'break-word',
    whiteSpace: 'normal'
  },
  titleHeader: {
    textAlign: 'center',
    fontWeight: 'bold',
    color: 'normal'
  },
  tableHeader: {
    height: 25
  },
  checkbox: {
    fontSize: 13,
    paddingTop: 40,
    paddingRight: 40,
    whiteSpace: 'nowrap',
  },
  buttons: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    justifyContent: 'flex-end',
    flexWrap: 'wrap',
  }
}

export default injectIntl(ApplicantForm)



// WEBPACK FOOTER //
// ./src/components/staff/ApplicantForm.js